import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { scheduleAPI } from '../services/api.ts';
import { format, parseISO } from 'date-fns';
import MapPicker from '../components/MapPicker.tsx';

interface ScheduleHistoryItem {
  id: number;
  date: string;
  time: string;
  status: string;
  address: string;
  location: string;
  completedBy?: string;
  completedDateTime?: string;
  workFlowStatusId?: number;
  notes?: string;
  images?: string[];
  contactNo?: string;
  pic?: string;
  scheduleDate?: string;
}

const ScheduleHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [historyItems, setHistoryItems] = useState<ScheduleHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  
  const PAGE_SIZE = 7;

  // Format date for display
  const formatDateForDisplay = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Format time for display
  const formatTimeForDisplay = (timeString: string): string => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // Function to handle address click
  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    setShowMapPicker(true);
  };

  // Get status text and color based on workFlowStatusId
  const getStatusDetails = (workFlowStatusId?: number): { text: string; colorClass: string } => {
    switch (workFlowStatusId) {
      case 0:
        return { text: 'Pending', colorClass: 'bg-yellow-100 text-yellow-800' };
      case 1:
        return { text: 'In Progress', colorClass: 'bg-blue-100 text-blue-800' };
      case 2:
        return { text: 'Completed', colorClass: 'bg-green-100 text-green-800' };
      default:
        return { text: 'Unknown', colorClass: 'bg-gray-100 text-gray-800' };
    }
  };

  // Fetch history items
  const fetchHistoryItems = useCallback(async (page: number, loadMore = false) => {
    if (!id) return;
    try {
      if (!loadMore) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const response = await scheduleAPI.getScheduleHistory(id, {
        PageNumber: page,
        PageSize: PAGE_SIZE
      });

      const newItems = response.data || [];

      if (loadMore) {
        setHistoryItems(prev => [...prev, ...newItems]);
      } else {
        setHistoryItems(newItems);
      }

      // Check if there might be more data
      setHasMore(newItems.length === PAGE_SIZE);
      setError(null);
    } catch (err) {
      console.error(`Error fetching history for schedule ${id}:`, err);
      setError('Failed to load history items. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [id]); // Only depend on id

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;

    // Disconnect previous observer if exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPageNumber(prev => prev + 1);
      }
    }, options);

    observerRef.current = observer;

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, isLoadingMore]);

  // Initial data fetch on mount
  useEffect(() => {
    fetchHistoryItems(1, false);
  }, [fetchHistoryItems]); // Run once on mount when fetchHistoryItems is defined

  // Handle page number changes for pagination
  useEffect(() => {
    // Skip the initial render (only react to page number changes)
    if (pageNumber > 1) {
      fetchHistoryItems(pageNumber, true);
    }
  }, [pageNumber, fetchHistoryItems]);

  // Render loading state
  if (loading && !isLoadingMore) {
    return (
      <div className="container-app py-6">
        <div className="flex items-center mb-6">
        
          <h1 className="text-2xl font-bold">Schedule History</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6 flex justify-center items-center">
          <div className="text-center">
            <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            <p className="mt-4 text-gray-600">Loading history...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && historyItems.length === 0) {
    return (
      <div className="container-app py-6">
        <div className="flex items-center mb-6">
       
          <h1 className="text-2xl font-bold">Schedule History</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center text-red-500">
            <p>{error}</p>
            <button 
              onClick={() => fetchHistoryItems(1, false)} 
              className="mt-4 btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-app py-6">
      <div className="flex items-center mb-6">

        <h1 className="text-2xl font-bold">
          Schedule History
        </h1>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">
            History Records ({historyItems.length})
          </h2>
        </div>
        
        {historyItems.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No history records found for this schedule.
          </div>
        ) : (
          <div className="divide-y">
            {historyItems.map((item, index) => {
              const isLastItem = index === historyItems.length - 1;
              const statusDetails = getStatusDetails(item.workFlowStatusId);
              
              return (
                <div 
                  key={`${item.id}-${index}`}
                  ref={isLastItem && hasMore ? lastItemRef : null}
                  className="p-4 hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="bg-blue-500 text-white p-1 rounded mr-2">
                          <span className="text-xs font-bold">{formatDateForDisplay(item.scheduleDate || item.date)}</span>
                        </div>
                        <span className="text-sm text-gray-600">{formatTimeForDisplay(item.time)}</span>
                        <span className="ml-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusDetails.colorClass}`}>
                            {statusDetails.text}
                          </span>
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{item.location}</h3>
                        <p 
                          className="text-sm text-primary cursor-pointer hover:underline flex items-center"
                          onClick={() => handleAddressClick(item.address)}
                        >
                          {item.address}
                          <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                          </svg>
                        </p>
                      </div>

                      {/* Contact Number with WhatsApp Link */}
                      {item.contactNo && (
                        <div className="mt-2 text-sm">
                          <p className="text-gray-600 flex items-center">
                            
                            <a 
                              href={`https://wa.me/${item.contactNo.replace(/\D/g, '')}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline flex items-center"
                            >
                              {item.contactNo}
                              <svg className="w-4 h-4 ml-1 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path fill="currentColor" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                              </svg>
                            </a>
                          </p>
                        </div>
                      )}

                      {/* Site Manager */}
                      {item.pic && (
                        <div className="mt-2 text-sm">
                          <p className="text-gray-600">
                            <span className="font-medium">Site Manager:</span> {item.pic}
                          </p>
                        </div>
                      )}
                      
                      {item.completedBy && item.completedDateTime && (
                        <div className="mt-2 text-sm">
                          <p className="text-gray-600">
                            Completed by {item.completedBy} on {formatDateForDisplay(item.completedDateTime)}
                          </p>
                        </div>
                      )}
                      
                      {item.notes && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                            <span className="font-medium">Notes:</span> {item.notes}
                          </p>
                        </div>
                      )}

                      {/* View Details Link */}
                      <div className="mt-3">
                        <Link
                          to={`/schedule/${item.id}`}
                          className="text-primary font-medium hover:underline text-sm inline-flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Details
                        </Link>
                      </div>
                    </div>
                    
                    {/* Images */}
                    {item.images && item.images.length > 0 && (
                      <div className="ml-4 flex items-center space-x-2">
                        {item.images.slice(0, 2).map((image, imgIndex) => (
                          <div key={imgIndex} className="relative">
                            <div className="w-16 h-16 rounded-md overflow-hidden bg-gray-100">
                              <img
                                src={image}
                                alt={`Schedule image ${imgIndex + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        ))}
                        
                        {item.images.length > 2 && (
                          <div className="bg-gray-200 rounded-md w-8 h-16 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">+{item.images.length - 2}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            
            {isLoadingMore && (
              <div className="p-4 text-center">
                <div className="inline-block animate-spin h-5 w-5 border-4 border-primary border-t-transparent rounded-full"></div>
                <p className="text-sm text-gray-600 mt-2">Loading more...</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Map Picker Modal */}
      {showMapPicker && (
        <MapPicker
          isOpen={showMapPicker}
          onClose={() => setShowMapPicker(false)}
          address={selectedAddress}
        />
      )}
    </div>
  );
};

export default ScheduleHistory; 